// frontend/src/components/ui/Select.tsx
// Radix @radix-ui/react-select wrapper matching the proto `.select` / `.opt` look.
// Presentational only — no data fetching, no mutations.

import * as RadixSelect from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/cn";

export interface SelectOption {
  value: string;
  label: string;
  /** Two-character emoji flag or image URL — rendered inline before the label */
  flag?: string;
}

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Minimum width of the trigger; defaults to min-w-[160px] */
  minWidth?: string;
  /**
   * Explicit accessible label for the trigger, used by FilterGroup labelling.
   * Only set when the surrounding context does not already provide a visible
   * label — do NOT set it to the placeholder text, which would permanently
   * override the accessible name even after a value is selected.
   */
  ariaLabel?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  disabled,
  className,
  minWidth = "min-w-[160px]",
  ariaLabel,
}: SelectProps): JSX.Element {
  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <RadixSelect.Trigger
        className={cn(
          // Proto .select .trigger
          "inline-flex items-center gap-2 h-9 px-3",
          "border border-border rounded-lg bg-surface text-[13px] text-text",
          minWidth,
          "hover:border-[#dcd9d2]",
          "focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none",
          "data-[state=open]:border-accent data-[state=open]:ring-[3px] data-[state=open]:ring-accent/[.14]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "transition-[border-color,box-shadow]",
          className,
        )}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon asChild>
          <ChevronDown
            className="ml-auto h-3.5 w-3.5 text-dim shrink-0 transition-transform duration-150 group-data-[state=open]:rotate-180"
            aria-hidden
          />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className={cn(
            // Proto .select .menu
            "z-20 bg-surface border border-border rounded-[10px] shadow-pop p-1.5",
            "max-h-72 overflow-auto",
            "w-[var(--radix-select-trigger-width)] min-w-[160px]",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          <RadixSelect.Viewport>
            {options.map((opt) => (
              <RadixSelect.Item
                key={opt.value}
                value={opt.value}
                className={cn(
                  // Proto .opt
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px]",
                  "cursor-pointer outline-none select-none",
                  "hover:bg-surface-2",
                  "data-[state=checked]:bg-accent-soft data-[state=checked]:text-accent data-[state=checked]:font-semibold",
                  "data-[highlighted]:bg-surface-2 data-[highlighted]:outline-none",
                )}
              >
                {opt.flag && (
                  <span aria-hidden className="shrink-0 text-[15px] leading-none">
                    {opt.flag}
                  </span>
                )}
                <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="ml-auto">
                  <Check className="h-3.5 w-3.5 text-accent" aria-hidden />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
